import { useEffect } from "react";
import type { FallbackProps } from "react-error-boundary";
import { hasPermission } from "wombat-global/src/utilities/permissions";

import WombatLogo from "../assets/wombat_logo.svg?react";
import { ReloadPromptInline } from "./ServiceWorkerPrompt";
import { LOCATION_RESTORE_KEY } from "../hooks/useLocalStorage";
import { auth } from "../services/firebase.service";
import { getDecodedAuthToken } from "../contexts/authentication/getDecodedAuthToken";
import { getPriorityTarget } from "../contexts/authentication/getPriorityLoginTarget";
import { getTargetLandingPath } from "../utilities/target-landing-path";

import "./404ErrorComponent.css";

const ErrorComponent: React.FC<FallbackProps> = ({ error }) => {
  useEffect(() => {
    localStorage.removeItem(LOCATION_RESTORE_KEY);
  }, []);

  useEffect(() => {
    // oxlint-disable-next-line no-console
    console.error(error);
  }, [error]);

  return (
    <main className="safety-error-page" aria-labelledby="safety-error-title">
      <div className="safety-error-art" aria-hidden="true">
        <div className="safety-error-ring" />
        <div className="safety-error-navy-circle" />
        <div className="safety-error-dots" />
        <div className="safety-error-orange-circle" />
      </div>

      <div className="safety-error-content">
        <WombatLogo
          className="safety-error-logo"
          role="img"
          aria-label="Wombat Safety Software"
        />
        <span className="safety-error-label">Error</span>
        <h1 id="safety-error-title" className="safety-error-title">
          Well, that wasn&apos;t
          <br />
          supposed to happen.
        </h1>
        <p className="safety-error-description">
          Something went wrong while loading this page. Your data is safe, and
          you can head back home to continue.
        </p>
        <button
          type="button"
          className="safety-error-home-button"
          onClick={async () => {
            localStorage.removeItem(LOCATION_RESTORE_KEY);
            try {
              await auth.authStateReady();
              const token = await getDecodedAuthToken();
              if (!token) {
                window.location.replace("/login");
                return;
              }

              const target = await getPriorityTarget(token.claims);
              window.location.replace(
                target
                  ? getTargetLandingPath(target, (_target, permissionFlag) =>
                      hasPermission(_target, token.claims, permissionFlag),
                    )
                  : "/no-access",
              );
            } catch (err) {
              // oxlint-disable-next-line no-console
              console.error(err);
              window.location.replace("/login");
            }
          }}
        >
          Back to Home
        </button>
        <ReloadPromptInline className="!mr-0 mt-4" />
        <p className="safety-error-support">
          If this keeps happening, contact{" "}
          <a href="mailto:support@wombat.software">support@wombat.software</a>.
        </p>
      </div>

      <nav className="safety-error-footer" aria-label="Recovery options">
        <a href="/home">home</a>
        <a href="/logout">logout</a>
      </nav>
    </main>
  );
};

export default ErrorComponent;
